import { api } from "@breakout/backend/convex/_generated/api"
import { ZERO_ADDRESS } from "@breakout/shared/constants"
import type { Maybe } from "@breakout/shared/types/market"
import { convexQuery } from "@convex-dev/react-query"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import type { Address } from "viem"

export type TimeRange = "1H" | "6H" | "1D" | "1W" | "MAX"

interface PriceHistoryArgs {
	marketAddress: Address
	bucketCount: number
	timeRange?: TimeRange
}

/**
 * Fetches price history for a market.
 *
 * Uses a Convex query (reactive, fast) instead of action.
 * Price points are computed by replaying indexed trade events.
 * Backend handles time-range filtering and downsampling.
 */
export function usePriceHistory(args: Maybe<PriceHistoryArgs>) {
	return useQuery({
		...convexQuery(api.indexer.public.getPriceHistory, {
			marketAddress: args?.marketAddress ?? ZERO_ADDRESS,
			bucketCount: args?.bucketCount ?? 2,
			timeRange: args?.timeRange ?? "MAX",
		}),
		enabled: !!args && args.bucketCount > 0,
		// Convex subscriptions are reactive - no polling needed
		// Data auto-updates when indexed trade events change
		placeholderData: keepPreviousData,
	})
}
